import {Banner} from '@primer/react'
import {Suspense, use} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {LazyThreadsDataLoader, useShouldLoadLazyThreadData} from './LazyThreadsDataLoader'
import {ThreadPreviewsContent} from './ThreadPreviewsContent'
import {IssueCommentsContent} from './IssueCommentsContent'
import {isReviewCommentsViewMode} from './CommentsViewControls'
import {CommentsContext, ViewMode as ViewModeValues} from './CommentsContext'
import type {ConversationItem} from './utils/build-conversation-items'

export interface CommentsProps {
  selectedReviewId: number | null
  handleQuoteReply: (quotedText: string) => void
  filteredConversationItems: ConversationItem[]
  /** The maximum number of latest comments shown when the limit is exceeded. */
  limitCount?: number
  /** Whether the number of comments exceeds the displayable limit. */
  limitExceeded?: boolean
  /**
   * Called after the user activates a thread preview's link to its comment in the diff.
   * Navigating to the comment is handled internally, so this is only for side effects the
   * consumer wants to layer on top (e.g. closing a panel that renders these comments).
   */
  onNavigateToDiffComment?: () => void
  /** The current file path name used to scope displayed comments. */
  pathName: string
  /** The login handle of the pull request author, used for issue comments rendering. */
  pullRequestAuthorLogin: string
  totalConversationItemsCount: number
}

/**
 * Renders pull request comments.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded with a skeleton fallback.
 */
export function Comments({
  selectedReviewId,
  handleQuoteReply,
  filteredConversationItems,
  limitCount,
  limitExceeded,
  onNavigateToDiffComment,
  pathName,
  pullRequestAuthorLogin,
  totalConversationItemsCount,
}: CommentsProps) {
  const {viewMode} = use(CommentsContext)
  const isReviewCommentsMode = isReviewCommentsViewMode(viewMode)
  const {shouldLazyLoadData, isLazyLoading, onLazyDataLoaded} = useShouldLoadLazyThreadData()
  const reviewIdFilter = isFeatureEnabled('prs_docked_comments_replies') ? selectedReviewId : null

  const filteredThreads = isReviewCommentsMode
    ? filteredConversationItems.filter(item => item.type === 'thread').map(item => item.data)
    : []

  return (
    <>
      {shouldLazyLoadData && <LazyThreadsDataLoader onLoaded={onLazyDataLoaded} />}
      {isReviewCommentsMode && totalConversationItemsCount > 0 && (
        <span style={{marginTop: '-4px'}} className="mb-2 fgColor-muted text-small text-center d-block">
          Showing {filteredConversationItems.length} of {totalConversationItemsCount} comments
        </span>
      )}
      {isReviewCommentsMode && limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
          className="tmp-mb-3"
        />
      )}
      <ErrorBoundary boundaryName="Comments" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          {viewMode === ViewModeValues.ThreadPreviews && isLazyLoading ? (
            <ThreadPreviewSkeleton /> // TODO: just use suspense
          ) : isReviewCommentsMode ? (
            // TODO: we should be able to render thread previews using IssueComments instead, allowing us to unify
            // on one code path. However, currently the issue comments component triggers that data to load via
            // `useLoadAllIssueCommentsAndReviews`.
            // Ideally we should be able to move the data loading into the filtered data hook and move that into the
            // issue comments component
            <ThreadPreviewsContent
              onNavigateToDiffComment={onNavigateToDiffComment}
              threads={filteredThreads}
              totalDiffCommentCount={totalConversationItemsCount}
              selectedReviewId={reviewIdFilter}
            />
          ) : (
            <IssueCommentsContent
              filteredConversationItems={filteredConversationItems}
              totalConversationItemsCount={totalConversationItemsCount}
              pathName={pathName}
              pullRequestAuthorLogin={pullRequestAuthorLogin}
              onNavigateToDiffComment={onNavigateToDiffComment}
              onQuoteReply={handleQuoteReply}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </>
  )
}
